if (!customElements.get('custom-slider')) {
  class CustomSlider extends HTMLElement {
    constructor() {
      super();
      this.sliders = [];
      this.breakpoint = parseInt(this.dataset.mobileBreakpoint, 10) || null;

      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      if (this.breakpoint) {
        this.onResize();
        window.addEventListener('resize', this.onResize);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
      window.removeEventListener('resize', this.onResize);
    }

    /* =========================
       RESPONSIVE CONTROL
    ========================= */
    onResize() {
      const isActive = window.innerWidth <= this.breakpoint;

      if (isActive) {
        this.init();
      } else {
        this.destroy();
      }
    }

    /* =========================
       INIT (AUTO DISCOVERY)
    ========================= */
    init() {
      if (this.sliders.length) return;

      const sliderEls = this.querySelectorAll('[data-slider]');

      sliderEls.forEach(el => {
        const flkty = new Flickity(el, this.getOptions(el));
        this.sliders.push({ el, flkty });
        // 🔥 HEIGHT FIX HOOK
        this.setTestimonialHeights(el, flkty);
      });

      this.bindSync();
      this.bindProgress();
      this.bindDots();
    }

    setTestimonialHeights(el, flkty) {
      if (!el.classList.contains('team-testimonials__grid')) return;

      const apply = () => {
        const cards = el.querySelectorAll(
          '.testimonial-card'
        );

        let maxHeight = 0;

        cards.forEach(card => {
          const imgWrap = card;
          if (!imgWrap) return;

          const height = imgWrap.getBoundingClientRect().height;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });

        if (!maxHeight) return;

        // Apply to all cards
        el.querySelectorAll('.testimonial-card').forEach(card => {
          card.style.minHeight = `${maxHeight}px`;
        });

        // Apply to Flickity viewport
        const viewport = el.querySelector('.flickity-viewport');
        if (viewport) {
          viewport.style.minHeight = `${maxHeight}px`;
        }
      };

      /* Flickity lifecycle */
      flkty.on('ready', () => requestAnimationFrame(apply));
      flkty.on('change', () => requestAnimationFrame(apply));

      /* Image load safety */
      el.querySelectorAll('img').forEach(img => {
        if (img.complete) return;
        img.addEventListener('load', apply);
      });

      /* Resize */
      window.addEventListener('resize', () => {
        requestAnimationFrame(apply);
      });

      setTimeout(apply, 2000);
    }


    /* =========================
       OPTIONS FROM DATA ATTRS
    ========================= */
    getOptions(el) {
      return {
        cellAlign: el.dataset.align || 'left',
        contain: el.dataset.contain !== 'false',
        fade: el.dataset.fade === 'true',
        draggable: el.dataset.draggable !== 'false',
        wrapAround: el.dataset.wrap === 'true',
        pageDots: false,
        prevNextButtons: false,
        autoPlay: el.dataset.autoplay === 'true'
          ? parseInt(el.dataset.speed, 10) || 3000
          : false
      };
    }

    /* =========================
       SYNC SLIDERS
    ========================= */
    bindSync() {
      if (this.sliders.length < 2) return;

      const masterItem = this.sliders.find(
        ({ el }) => el.dataset.control === 'master'
      );

      if (!masterItem) return;

      const master = masterItem.flkty;

      const subjects = this.sliders.filter(
        ({ el }) => el.dataset.control === 'subject'
      );

      if (!subjects.length) return;

      master.on('change', index => {
        subjects.forEach(({ flkty }) => {
          // 🔑 required for Flickity sync
          flkty.select(index, false, true);
        });
      });
    }

    /* =========================
       PROGRESS BAR (OPTIONAL)
    ========================= */
    bindProgress() {
      const bar = this.querySelector('[data-progress]');
      if (!bar || !this.sliders.length) return;

      this.sliders[0].flkty.on('scroll', progress => {
        const percent = Math.max(0, Math.min(1, progress)) * 100;
        bar.style.width = `${percent}%`;
      });
    }

    /* =========================
       DOTS (HTML ONLY)
    ========================= */
    bindDots() {
      const dots = this.querySelectorAll('[data-dot]');
      if (!dots.length || !this.sliders.length) return;

      // find master (same logic as bindSync)
      const masterItem = this.sliders.find(({ el }) => el.dataset.control === 'master') || this.sliders[0];

      const master = masterItem.flkty;

      dots.forEach(dot => {
        dot.addEventListener('click', () => {
          master.select(+dot.dataset.index);
        });
      });

      master.on('change', index => {
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      });
    }


    /* =========================
       🔥 DESTROY (SAFE + CLEAN)
    ========================= */
    destroy() {
      this.sliders.forEach(({ flkty }) => {
        flkty.destroy();
      });

      this.sliders = [];

      const bar = this.querySelector('[data-progress]');
      if (bar) bar.style.width = '0%';
    }
  }

  customElements.define('custom-slider', CustomSlider);
}


class TimeLine extends HTMLElement {
  connectedCallback() {
    this.finalSlideHeight = null;
    this.carousel = this.querySelector('.js-timeline-carousel');
    this.slides = Array.from(this.querySelectorAll('.timeline-slide'));

    if (!this.carousel || !this.slides.length) return;

    /* Store slides count */
    this.slideCount = this.slides.length;
    this.style.setProperty('--slides', this.slideCount);

    /* Normalize slide heights */
    this.setUniformSlideHeight();

    /* Init Flickity */
    this.flkty = new Flickity(this.carousel, {
      cellAlign: 'left',
      contain: true,
      draggable: false,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: false
    });

    this.currentIndex = 0;

    /* Bind events */
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    window.addEventListener('scroll', this.onScroll);
    window.addEventListener('resize', this.onResize);
  }

  /* --------------------------------
     HEIGHT CALCULATION
  -------------------------------- */

   resetHeights() {
    this.slides.forEach(slide => {
      slide.style.removeProperty('height');
    });

    this.style.removeProperty('height');
  }

  setUniformSlideHeight() {
    /* Reset heights to measure correctly */
    this.resetHeights();

    /* Find max height */
    const maxHeight = Math.max(
      ...this.slides.map(slide => slide.offsetHeight)
    );

    this.finalSlideHeight = maxHeight * 2;

    /* Apply to all slides */
    this.slides.forEach(slide => {
      slide.style.height = `${this.finalSlideHeight}px`;
    });

    /* Set scrollable section height */
    this.style.height = `${this.finalSlideHeight * this.slideCount}px`;
  }


  onResize() {
    this.setUniformSlideHeight();

    if (this.flkty) {
      this.flkty.resize();
    }
  }

  /* --------------------------------
     SCROLL → SLIDE LOGIC
  -------------------------------- */

  getHeaderHeight() {
    const headerHeight =
      parseFloat(
        getComputedStyle(document.body).getPropertyValue('--header-height')
      ) || 0;

    return Math.max(0, headerHeight - 45);
  }

  onScroll() {
    const rect = this.getBoundingClientRect();
    const headerHeight = this.getHeaderHeight();

    /* Start when sticky engages */
    if (rect.top <= headerHeight && rect.bottom > window.innerHeight + headerHeight) {
      const scrolled = Math.max(0, Math.abs(rect.top - headerHeight));
      const slideHeight = this.slides[0].offsetHeight / 2;

      const index = Math.min(
        this.slideCount - 1,
        Math.floor(scrolled / slideHeight)
      );

      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this.flkty.select(index);
      }
    }
  }
}

customElements.define('time-line', TimeLine);